import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import {
  formatIsoDatetimeForConditionDisplay,
  getConditionDatetimeApplyHighlightColor,
} from '~/components/Common/Elements/ConditionValueDatetimeField';
import PlaceIdCoordinateLookupHelper, {
  PLACE_ID_CONDITION_KEY,
} from '~/components/Promo/PlaceIdCoordinateLookupHelper';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import VoucherConditionValueEditCell from '~/components/Vouchers/VoucherConditionValueEditCell';

import type { PromoConditionTypeConfig, PromoMeta } from '~/services/PromoService';
import { parseDayjs } from '~/services/TimeService';

import {
  ApplyTypeEditCell,
  type ConditionApplyType,
  ConditionTypeEditCell,
  RewardFilterEditCell,
  TextEditCell,
  defaultConditionApplyTypes,
  formatApplyTypeLabel,
  formatConditionLabel,
  voucherConditionTypes,
} from './VoucherConditionEditCells';
import { copyVoucherCondition } from './voucherConditionCopyUtils';

type VoucherCondition = definitions['VoucherConditionV1'];
type VoucherReward = definitions['VoucherRewardV1'];

type Props = {
  conditions: Array<VoucherCondition>;
  rewards: Array<VoucherReward>;
  onChange: (conditions: Array<VoucherCondition>) => void;
  disabled?: boolean;
  showAdvanceDescription?: boolean;
  /**
   * When set, controls visibility of the "Details" column (condition type help text).
   * If omitted, the column is shown when "advanced description" is on and/or a row is in edit mode
   * (so callers that only set `showAdvanceDescription` still see condition guidance without editing).
   */
  showDetailsColumn?: boolean;
  /** Rendered beside the "Add condition" button (e.g. toggle for the Details column). */
  additionalConditionActions?: React.ReactNode;
  /** When set, only these condition type keys (plus any types already used in rows) appear in the type picker. */
  allowedConditionTypeKeys?: Array<string>;
  includeRewardIdFilters?: boolean;
  /**
   * Extra condition-type rows merged with `promoMeta.voucher_condition_types` by `key` (later entries overlay).
   * Pass e.g. `voucher_auto_assignment_rule_options.rule_condition_types_supported` so `conditionValueType` and
   * apply types from the auto-assign meta apply on the voucher auto-assign rule form.
   */
  additionalVoucherConditionTypeConfigs?: NonNullable<
    NonNullable<PromoMeta['voucher_auto_assignment_rule_options']>['rule_condition_types_supported']
  >;
};

export default function VoucherConditions({
  conditions,
  rewards,
  onChange,
  showAdvanceDescription,
  showDetailsColumn,
  additionalConditionActions,
  disabled = false,
  allowedConditionTypeKeys,
  includeRewardIdFilters = true,
  additionalVoucherConditionTypeConfigs,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { promoMeta } = usePromoMeta();
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [newConditionIds, setNewConditionIds] = useState<Array<string>>([]);

  const baseVoucherConditionTypeConfigs = useMemo(
    () => promoMeta?.voucher_condition_types ?? [],
    [promoMeta?.voucher_condition_types],
  );

  const voucherConditionTypeConfigs = useMemo((): Array<PromoConditionTypeConfig> => {
    const base = baseVoucherConditionTypeConfigs.map((c) => ({ ...c })) as Array<PromoConditionTypeConfig>;
    const byKey = new Map<string, PromoConditionTypeConfig>();
    base.forEach((c) => {
      byKey.set(String(c.key), c);
    });
    (additionalVoucherConditionTypeConfigs ?? []).forEach((extra) => {
      const key = String(extra.key);
      const current = byKey.get(key);
      if (current) {
        byKey.set(key, { ...current, ...extra } as PromoConditionTypeConfig);
      } else {
        byKey.set(key, extra as PromoConditionTypeConfig);
      }
    });
    return Array.from(byKey.values());
  }, [additionalVoucherConditionTypeConfigs, baseVoucherConditionTypeConfigs]);
  const voucherConditionTypeOptions = useMemo(() => {
    const fromMeta: Array<VoucherCondition['condition_type']> =
      voucherConditionTypeConfigs.length > 0
        ? (voucherConditionTypeConfigs.map((c) => c.key) as Array<VoucherCondition['condition_type']>)
        : [...voucherConditionTypes];
    const inUse = new Set(conditions.map((c) => c.condition_type));
    if (!allowedConditionTypeKeys?.length) {
      return fromMeta;
    }
    const allow = new Set(allowedConditionTypeKeys);
    const filtered = fromMeta.filter((key) => allow.has(key) || inUse.has(key));
    return filtered.length > 0 ? filtered : fromMeta;
  }, [allowedConditionTypeKeys, conditions, voucherConditionTypeConfigs]);

  const voucherConditionTypeConfigMap = useMemo(() => {
    const map = new Map<string, PromoConditionTypeConfig>();
    voucherConditionTypeConfigs.forEach((config) => {
      map.set(String(config.key), config);
    });
    return map;
  }, [voucherConditionTypeConfigs]);

  const conditionApplyTypeMap = useMemo(() => {
    const map = new Map<string, Array<ConditionApplyType>>();
    voucherConditionTypeConfigs.forEach((config) => {
      map.set(String(config.key), (config.conditionApplyTypes ?? []) as Array<ConditionApplyType>);
    });
    return map;
  }, [voucherConditionTypeConfigs]);

  useEffect(() => {
    setNewConditionIds((prev) =>
      prev.filter((id) => conditions.some((condition) => condition.id_voucher_condition === id)),
    );
  }, [conditions]);

  const rewardOptions = useMemo(() => {
    const baseOptions = rewards.map((reward, index) => ({
      id: reward.id_voucher_reward,
      label: `Reward ${index + 1}${Number.isFinite(reward.reward_amount) ? ` (${reward.reward_amount})` : ''}`,
    }));

    const existingIds = new Set(baseOptions.map((option) => option.id));
    const extras = new Map<string, string>();
    conditions.forEach((condition) => {
      (condition.voucher_reward_ids ?? []).forEach((rewardId) => {
        if (!existingIds.has(rewardId)) {
          extras.set(rewardId, `Unknown Reward (${rewardId})`);
        }
      });
    });

    const extraOptions = Array.from(extras.entries()).map(([id, label]) => ({ id, label }));
    return [...baseOptions, ...extraOptions];
  }, [conditions, rewards]);

  const handleAddCondition = useCallback(() => {
    const newId = uuid();
    const defaultConditionType = voucherConditionTypeOptions[0] ?? voucherConditionTypes[0];
    const configuredApplyTypes = conditionApplyTypeMap.get(defaultConditionType) ?? [];
    const defaultApplyTypes = configuredApplyTypes.length > 0 ? configuredApplyTypes : defaultConditionApplyTypes;
    onChange([
      ...conditions,
      {
        id_voucher_condition: newId,
        condition_type: defaultConditionType,
        condition_value: '',
        condition_apply_type: defaultApplyTypes[0] ?? 'NONE',
        voucher_reward_ids: [],
        customized_message: '',
      },
    ]);
    setNewConditionIds((prev) => [...prev, newId]);
    setRowModesModel((prev) => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'condition_value' } }));
  }, [conditionApplyTypeMap, conditions, onChange, voucherConditionTypeOptions]);

  const handleEditClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, []);

  const handleSaveClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  }, []);

  const handleCancelClick = useCallback(
    (id: GridRowId) => {
      if (newConditionIds.includes(String(id))) {
        onChange(conditions.filter((condition) => condition.id_voucher_condition !== id));
        setNewConditionIds((prev) => prev.filter((conditionId) => conditionId !== id));
      }
      setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));
    },
    [conditions, newConditionIds, onChange],
  );

  const handleDeleteCondition = useCallback(
    (conditionId: string) => {
      const next = conditions.filter((condition) => condition.id_voucher_condition !== conditionId);
      onChange(next);
    },
    [conditions, onChange],
  );

  const handleCopyCondition = useCallback(
    (condition: VoucherCondition) => {
      const copiedCondition = copyVoucherCondition(condition);
      onChange([...conditions, copiedCondition]);
      setNewConditionIds((prev) => [...prev, copiedCondition.id_voucher_condition]);
      setRowModesModel((prev) => ({
        ...prev,
        [copiedCondition.id_voucher_condition]: { mode: GridRowModes.Edit, fieldToFocus: 'condition_value' },
      }));
    },
    [conditions, onChange],
  );

  const validateCondition = useCallback(
    (condition: VoucherCondition) => {
      if (!condition.condition_type) {
        enqueueSnackbar('Condition type is required', { variant: 'error' });
        return false;
      }
      if (!condition.condition_value?.trim()) {
        enqueueSnackbar('Condition value is required', { variant: 'error' });
        return false;
      }
      const typeConfig = voucherConditionTypeConfigMap.get(condition.condition_type);
      if (typeConfig?.conditionValueType === 'datetime' && !parseDayjs(condition.condition_value).isValid()) {
        enqueueSnackbar('Condition value must be a valid date and time', { variant: 'error' });
        return false;
      }
      return true;
    },
    [enqueueSnackbar, voucherConditionTypeConfigMap],
  );

  const processRowUpdate = useCallback(
    (updatedRow: VoucherCondition) => {
      // Strip virtual DataGrid fields that would cause API validation errors
      const { condition_details: _virtualGridField, ...cleanRow } = updatedRow as VoucherCondition & {
        condition_details?: unknown;
      };
      void _virtualGridField;

      const configuredApplyTypes = conditionApplyTypeMap.get(cleanRow.condition_type) ?? [];
      const allowedApplyTypes = configuredApplyTypes.length > 0 ? configuredApplyTypes : defaultConditionApplyTypes;
      const currentApplyType = cleanRow.condition_apply_type ?? 'NONE';
      const allowedForIncludes = allowedApplyTypes as ReadonlyArray<VoucherCondition['condition_apply_type']>;
      const normalizedApplyType = allowedForIncludes.includes(currentApplyType)
        ? currentApplyType
        : allowedForIncludes[0] ?? 'NONE';
      const normalizedRow =
        normalizedApplyType === cleanRow.condition_apply_type
          ? cleanRow
          : { ...cleanRow, condition_apply_type: normalizedApplyType };

      if (!validateCondition(normalizedRow)) {
        throw new Error('Validation failed');
      }
      const next = conditions.map((condition) =>
        condition.id_voucher_condition === normalizedRow.id_voucher_condition ? normalizedRow : condition,
      );
      onChange(next);
      setNewConditionIds((prev) => prev.filter((id) => id !== normalizedRow.id_voucher_condition));
      return normalizedRow;
    },
    [conditionApplyTypeMap, conditions, enqueueSnackbar, onChange, validateCondition],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update condition', { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  /** Keep row edit mode when focus leaves the grid (e.g. Details column toggle); commit only via Save. */
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const isAnyRowEditing = Object.values(rowModesModel).some((model) => model.mode === GridRowModes.Edit);

  const detailsColumnVisible =
    showDetailsColumn !== undefined ? showDetailsColumn : isAnyRowEditing || Boolean(showAdvanceDescription);
  /** True while a row is in edit mode and the type picker is set to PLACE-ID (parent `conditions` updates only on save). */
  const [placeIdTypeSelectedInEdit, setPlaceIdTypeSelectedInEdit] = useState(false);
  useEffect(() => {
    if (!isAnyRowEditing) {
      setPlaceIdTypeSelectedInEdit(false);
    }
  }, [isAnyRowEditing]);

  const handleConditionTypeSelectWhileEditing = useCallback((nextType: string) => {
    setPlaceIdTypeSelectedInEdit(nextType === PLACE_ID_CONDITION_KEY);
  }, []);

  const columns = useMemo<Array<GridColDef<VoucherCondition>>>(() => {
    const baseColumns: Array<GridColDef<VoucherCondition>> = [
      {
        field: 'condition_type',
        headerName: 'Type',
        minWidth: 120,
        flex: 2,
        editable: true,
        renderCell: (params) => {
          if (!params.row.condition_type) {
            return '-';
          }
          const config = voucherConditionTypeConfigMap.get(params.row.condition_type);
          const details = config?.longDescription || config?.name;
          return (
            <Stack spacing={0.5}>
              <Typography variant="body2">{formatConditionLabel(params.row.condition_type)}</Typography>
              {detailsColumnVisible && details ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  {details}
                </Typography>
              ) : null}
            </Stack>
          );
        },
        renderEditCell: (params) => (
          <Stack spacing={0.5} sx={{ width: '100%', py: 0.5 }}>
            <ConditionTypeEditCell
              {...params}
              conditionTypes={voucherConditionTypeOptions}
              onConditionTypeChange={handleConditionTypeSelectWhileEditing}
            />
            {detailsColumnVisible
              ? (() => {
                  const conditionType = (params.value ??
                    params.row.condition_type) as VoucherCondition['condition_type'];
                  const config = conditionType ? voucherConditionTypeConfigMap.get(conditionType) : undefined;
                  const details = config?.longDescription || config?.name;
                  if (!details) {
                    return null;
                  }
                  return (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                      {details}
                    </Typography>
                  );
                })()
              : null}
          </Stack>
        ),
      },
      {
        field: 'condition_apply_type',
        headerName: 'Apply',
        minWidth: 96,
        flex: 1,
        editable: true,
        renderCell: (params) =>
          params.row.condition_apply_type ? formatApplyTypeLabel(params.row.condition_apply_type) : '-',
        renderEditCell: (params) => (
          <ApplyTypeEditCell
            {...params}
            applyTypes={conditionApplyTypeMap.get(params.row.condition_type) ?? defaultConditionApplyTypes}
          />
        ),
      },
      {
        field: 'condition_value',
        headerName: 'Value',
        minWidth: 150,
        flex: 2,
        editable: true,
        renderCell: (params) => {
          const config = params.row.condition_type
            ? voucherConditionTypeConfigMap.get(params.row.condition_type)
            : undefined;
          const raw = params.row.condition_value || '';
          const display =
            config?.conditionValueType === 'datetime' && raw ? formatIsoDatetimeForConditionDisplay(raw) : raw || '-';
          const highlight =
            config?.conditionValueType === 'datetime' && raw
              ? getConditionDatetimeApplyHighlightColor(params.row.condition_apply_type, raw)
              : undefined;
          return (
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={raw}
              {...(highlight ? { color: highlight } : {})}
            >
              {display}
            </Typography>
          );
        },
        renderEditCell: (params) => {
          const config = params.row.condition_type
            ? voucherConditionTypeConfigMap.get(params.row.condition_type)
            : undefined;
          return (
            <VoucherConditionValueEditCell
              {...params}
              isDatetime={config?.conditionValueType === 'datetime'}
              suggestedConditionValues={config?.suggestedConditionValues}
            />
          );
        },
        preProcessEditCellProps: (params) => {
          const config = params.row.condition_type
            ? voucherConditionTypeConfigMap.get(params.row.condition_type)
            : undefined;
          const cellValue =
            typeof params.props.value === 'string' ? params.props.value : String(params.props.value ?? '');
          if (config?.conditionValueType === 'datetime') {
            const hasError = !cellValue.trim() || !parseDayjs(cellValue).isValid();
            return { ...params.props, error: hasError };
          }
          const hasError = !cellValue.trim();
          return { ...params.props, error: hasError };
        },
      },
    ];

    const rewardColumns: Array<GridColDef<VoucherCondition>> = includeRewardIdFilters
      ? [
          {
            field: 'voucher_reward_ids',
            headerName: 'Rewards',
            minWidth: 120,
            flex: 2,
            editable: true,
            renderCell: (params) => {
              const ids = params.row.voucher_reward_ids ?? [];
              if (!ids.length) {
                return '-';
              }
              const labels = ids.map((id) => rewardOptions.find((option) => option.id === id)?.label || id);
              return labels.join(', ');
            },
            renderEditCell: (params) => <RewardFilterEditCell {...params} rewardOptions={rewardOptions} />,
          },
        ]
      : [];

    const trailingColumns: Array<GridColDef<VoucherCondition>> = [
      {
        field: 'customized_message',
        headerName: 'Message',
        minWidth: 120,
        flex: 2,
        editable: true,
        renderCell: (params) => params.row.customized_message || '-',
        renderEditCell: (params) => <TextEditCell {...params} />,
      },
      {
        field: 'actions',
        headerName: '',
        width: 132,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isEditing = rowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={1}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save condition"
                    size="small"
                    type="button"
                    onClick={() => handleSaveClick(params.id)}
                    disabled={disabled}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel condition edit"
                    size="small"
                    type="button"
                    onClick={() => handleCancelClick(params.id)}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton
                    aria-label="Edit condition"
                    size="small"
                    type="button"
                    onClick={() => handleEditClick(params.id)}
                    disabled={disabled}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <Tooltip title="Copy condition">
                    <span>
                      <IconButton
                        aria-label="Copy condition"
                        size="small"
                        type="button"
                        onClick={() => handleCopyCondition(params.row)}
                        disabled={disabled || isEditing}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    aria-label="Delete condition"
                    size="small"
                    type="button"
                    onClick={() => handleDeleteCondition(params.row.id_voucher_condition || '')}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          );
        },
      },
    ];

    return [...baseColumns, ...rewardColumns, ...trailingColumns];
  }, [
    conditionApplyTypeMap,
    detailsColumnVisible,
    disabled,
    handleCancelClick,
    handleCopyCondition,
    handleDeleteCondition,
    handleEditClick,
    handleConditionTypeSelectWhileEditing,
    handleSaveClick,
    includeRewardIdFilters,
    rewardOptions,
    rowModesModel,
    voucherConditionTypeConfigMap,
    voucherConditionTypeOptions,
  ]);

  const hasPlaceIdCondition = conditions.some((c) => (c.condition_type as string) === PLACE_ID_CONDITION_KEY);
  const showPlaceIdHelper = hasPlaceIdCondition || placeIdTypeSelectedInEdit;

  return (
    <Box>
      {showAdvanceDescription && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          All Conditions need to be met to receive the voucher.
        </Typography>
      )}
      <DataGrid
        autoHeight
        density="compact"
        rows={conditions}
        columns={columns}
        getRowId={(row) => `${row.id_voucher_condition}`}
        getRowHeight={() => 'auto'}
        disableRowSelectionOnClick
        hideFooter
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        onRowEditStop={handleRowEditStop}
        sx={{
          backgroundColor: 'background.paper',
          width: '100%',
          maxWidth: '100%',
          '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.75rem', lineHeight: 1.2 },
          '& .MuiDataGrid-cell': { fontSize: '0.8125rem', py: 0.5 },
        }}
      />
      {showPlaceIdHelper && (
        <Box mt={2}>
          <PlaceIdCoordinateLookupHelper />
        </Box>
      )}
      <Box mt={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddCondition}
            disabled={disabled}
          >
            Add condition
          </Button>
          {additionalConditionActions}
        </Stack>
      </Box>
    </Box>
  );
}
